import * as i0 from '@angular/core';
import { InjectionToken, Injectable, Inject, Component, Input, inject, NgModule, isDevMode } from '@angular/core';
import 'reflect-metadata';
import * as i1 from '@engineering11/web-api-firebase';
import { CollectionRepository } from '@engineering11/web-api-firebase';
import { animationFadeInOut } from '@engineering11/ui-lib/e11-animations';
import { E11TemplateMap, E11UtilModule } from '@engineering11/ui-lib/e11-ui-utils';
import { isNotNil, isNil, isTrue } from '@engineering11/utility';
import * as i1$2 from '@engineering11/web-api-error';
import { e11Error } from '@engineering11/web-api-error';
import { combineLatest, interval, from, Subject, takeUntil, firstValueFrom } from 'rxjs';
import * as i1$1 from '@angular/router';
import { RouterModule } from '@angular/router';
import { ComponentStore } from '@ngrx/component-store';
import { tapResponse } from '@ngrx/operators';
import { map, filter, distinctUntilChanged, switchMap, withLatestFrom, tap } from 'rxjs/operators';
import * as i1$3 from '@engineering11/ui-lib/e11-toast';
import { ToastDismissType } from '@engineering11/ui-lib/e11-toast';
import * as i4 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i3 from '@ngx-translate/core';
import { TranslateModule } from '@ngx-translate/core';
import { provideRemoteConfig, getRemoteConfig } from '@angular/fire/remote-config';

const transformerDecorator = (symbol, defaultValue = true) => {
    return (target, propertyKey) => {
        let props;
        if (target.hasOwnProperty('__props__')) {
            props = target['__props__'];
        }
        else {
            props = target['__props__'] = [];
        }
        if (!props.includes(propertyKey)) {
            props.push(propertyKey);
        }
        Reflect.defineMetadata(symbol, defaultValue, target, propertyKey);
    };
};
const NumberKey = Symbol('REMOTE_CONFIG_NUMBER');
const StringKey = Symbol('REMOTE_CONFIG_STRING');
const BooleanKey = Symbol('REMOTE_CONFIG_BOOL');
const JsonKey = Symbol('REMOTE_CONFIG_JSON');
const DefaultKey = Symbol('REMOTE_CONFIG_DEFAULT');
const Number = () => transformerDecorator(NumberKey);
const String = () => transformerDecorator(StringKey);
const Boolean = () => transformerDecorator(BooleanKey);
const Json = () => transformerDecorator(JsonKey);
const Default = (defaultValue) => transformerDecorator(DefaultKey, defaultValue);
const isNumber = (target, propertyName) => Reflect.getMetadata(NumberKey, target, propertyName) || false;
const isString = (target, propertyName) => Reflect.getMetadata(StringKey, target, propertyName) || false;
const isBoolean = (target, propertyName) => Reflect.getMetadata(BooleanKey, target, propertyName) || false;
const isJson = (target, propertyName) => Reflect.getMetadata(JsonKey, target, propertyName) || false;
const getDefault = (target, propertyName) => (Reflect.getMetadata(DefaultKey, target, propertyName) || undefined);
/**
 * They should declare:
 * 1. The default values - as first-class objects if needed
 * 2. The types of the values: json, string, boolean, number?
 *
 * We should handle:
 * 1. Passing in those default values.
 * 2. Parsing out the correct types to get the config object in the form they expect.
 *
 */

const CONFIG_CONSTRUCTOR = new InjectionToken('ConfigConstructor');
const CONFIG_COLLECTION = 'configuration/';
const MAIN_CONFIG_DOC_ID = 'main-config';
const CONFIG_ERRORS = Object.freeze({
    MAIN_CONFIG_NOT_FOUND: 'config/main-config-not-found',
});
const BASE_SDK_IMAGE_URL = 'https://content.engineering11.com/assets/images/';
const DEFAULT_MAINTENANCE_DURATION = 20; // minutes
const DEFAULT_CONFIG_ROUTE = 'config';
const DEFAULT_HOME_ROUTE = '/';

/**
 * For getting configuration that loads when your app loads.
 * This is for configuration which might change your UI and hence you do not want it updating in front of your users
 *
 * Ensure module is initialised in base app
 * https://github.com/angular/angularfire/blob/master/docs/remote-config/getting-started.md
 */
class BaseConfigService {
    constructor(remoteConfigService, configConstructor) {
        this.remoteConfigService = remoteConfigService;
        this.configConstructor = configConstructor;
    }
    async getAll() {
        if (!this.config) {
            this.config = await this.buildConfig();
        }
        return this.config;
    }
    async getByKey(key) {
        if (!this.config) {
            this.config = await this.buildConfig();
        }
        return this.config[key];
    }
    async buildConfig() {
        const constructor = this.configConstructor;
        const remoteConfigData = await this.remoteConfigService.getAll();
        const props = constructor.prototype['__props__'];
        const config = new constructor();
        for (const prop of props) {
            const propValue = remoteConfigData?.[prop];
            if (propValue === undefined) {
                config[prop] = getDefault(config, prop);
                continue;
            }
            if (isNumber(config, prop)) {
                config[prop] = propValue.asNumber();
            }
            if (isBoolean(config, prop)) {
                config[prop] = propValue.asBoolean();
            }
            if (isString(config, prop)) {
                config[prop] = propValue.asString();
            }
            if (isJson(config, prop)) {
                config[prop] = JSON.parse(propValue.asString());
            }
        }
        return config;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: BaseConfigService, deps: [{ token: i1.RemoteConfigService }, { token: CONFIG_CONSTRUCTOR }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: BaseConfigService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: BaseConfigService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: i1.RemoteConfigService }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [CONFIG_CONSTRUCTOR]
                }] }] });

class BaseFeatureService {
    constructor(configService) {
        this.configService = configService;
    }
    getAll() {
        return this.configService.getByKey('features');
    }
    /**
     *
     * @param key
     * @returns
     */
    async getFeature(key) {
        const features = await this.configService.getByKey('features');
        return features[key];
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: BaseFeatureService, deps: [{ token: BaseConfigService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: BaseFeatureService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: BaseFeatureService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: BaseConfigService }] });

function getSecondsTilMaintenance(scheduledMaintenanceTime) {
    if (!scheduledMaintenanceTime)
        return undefined;
    return Math.round((scheduledMaintenanceTime.getTime() - new Date().getTime()) / 1000);
}

class BaseConfigProvider {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: BaseConfigProvider, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: BaseConfigProvider, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: BaseConfigProvider, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });
/**
 * @deprecated use BaseConfigProvider - less name clashing
 * TODO: on breaking change, delete
 */
class ConfigProvider extends BaseConfigProvider {
}

const CONFIG_ROUTES = Object.freeze({ MAINTENANCE: 'maintenance' });
class ConfigNavigationService {
    constructor(router, configProvider) {
        this.router = router;
        this.configProvider = configProvider;
        this.baseRoute = this.configProvider.config.baseConfigRoute ?? DEFAULT_CONFIG_ROUTE;
        this.maintenanceRoute = `/${this.baseRoute}/${CONFIG_ROUTES.MAINTENANCE}`;
        this.home = this.configProvider.config.homeRoute ?? DEFAULT_HOME_ROUTE;
    }
    toMaintenance() {
        return this.router.navigateByUrl(this.maintenanceRoute);
    }
    async routeWithConfig(url, config) {
        await this.router.navigateByUrl(url); // Necessary for query params to function properly; `router.navigate([url])` does not work
        if (config?.fullReload)
            location.reload();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ConfigNavigationService, deps: [{ token: i1$1.Router }, { token: BaseConfigProvider }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ConfigNavigationService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ConfigNavigationService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: i1$1.Router }, { type: BaseConfigProvider }] });

class ClientConfigRepository extends CollectionRepository {
    constructor() {
        super(...arguments);
        this.COLLECTION = CONFIG_COLLECTION;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ClientConfigRepository, deps: null, target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ClientConfigRepository, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ClientConfigRepository, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });

class MainConfigNotFoundError extends Error {
}
/**
 * For getting configuration you want your app to listen to
 */
class ListenConfigService {
    constructor(repository, configProvider) {
        this.repository = repository;
        this.configProvider = configProvider;
    }
    /**
     * Fetches the main configuration object once.
     * The main purpose of the ListenConfigService is for configuration that changes in the UI immediately, so this method should be the exception, not the norm.
     * Using @listenMainConfiguration is suggested for most purposes.
     * @returns
     */
    getMainConfiguration() {
        const mainConfig$ = this.repository.get(MAIN_CONFIG_DOC_ID);
        return mainConfig$.pipe(map((res) => {
            if (!res)
                throw e11Error(configNotFoundErrorData);
            return res;
        }));
    }
    /**
     * Returns an observable that emits changes to the main configuration object
     * @throws an error if the main configuration object is not found - it should be present in all environments
     * @returns
     */
    listenMainConfiguration() {
        const mainConfig$ = this.repository.getValueChanges(MAIN_CONFIG_DOC_ID);
        return mainConfig$.pipe(map((res) => {
            if (!res)
                throw e11Error(configNotFoundErrorData);
            return res;
        }));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ListenConfigService, deps: [{ token: ClientConfigRepository }, { token: BaseConfigProvider }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ListenConfigService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ListenConfigService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: ClientConfigRepository }, { type: BaseConfigProvider }] });
const configNotFoundErrorData = {
    type: CONFIG_ERRORS.MAIN_CONFIG_NOT_FOUND,
    title: `The main configuration document of id ${MAIN_CONFIG_DOC_ID} does not exist in the ${CONFIG_COLLECTION} collection`,
    level: 'none', // ! Suppressed for now because the user being disconnected will throw this error
};

const initialState = {
    staticConfigLoaded: false,
    listenConfigLoaded: false,
    listenConfig: null,
    staticConfig: null,
    preMaintenanceRoute: null,
};
class ConfigStore extends ComponentStore {
    constructor(errorHandler, logger, configService, listenConfigService, configNavigationService, router) {
        super(initialState);
        this.errorHandler = errorHandler;
        this.logger = logger;
        this.configService = configService;
        this.listenConfigService = listenConfigService;
        this.configNavigationService = configNavigationService;
        this.router = router;
        // SELECTORS
        this.getState = this.select((s) => s);
        this.staticConfig$ = this.select((s) => s.staticConfig);
        this.features$ = this.select((s) => s.staticConfig?.features);
        this.staticConfigLoaded$ = this.select((s) => s.staticConfigLoaded);
        this.listenConfig$ = this.select((s) => s.listenConfig);
        this.listenConfigLoaded$ = this.select((s) => s.listenConfigLoaded);
        this.maintenancePageOn$ = this.select(this.listenConfig$, (listenConfig) => listenConfig?.maintenancePageOn);
        this.scheduledMaintenanceTime$ = this.select(this.listenConfig$, (listenConfig) => listenConfig?.scheduledMaintenanceTime);
        this.maintenanceDuration$ = this.select(this.listenConfig$, (listenConfig) => listenConfig?.maintenanceDuration ?? DEFAULT_MAINTENANCE_DURATION);
        this.routeOnMaintenance$ = this.select(this.listenConfig$, (listenConfig) => listenConfig?.routeOnMaintenance ?? false);
        this.maintenanceFullReload$ = this.select(this.listenConfig$, (listenConfig) => listenConfig?.maintenanceFullReload ?? false);
        this.preMaintenanceRoute$ = this.select((s) => s?.preMaintenanceRoute);
        /** Refires every second or when scheduledMaintenanceTime changes */
        this.secondsTilMaintenance$ = combineLatest([interval(1000), this.scheduledMaintenanceTime$]).pipe(map(([_, scheduledMaintenanceTime]) => getSecondsTilMaintenance(scheduledMaintenanceTime)));
        this.showMaintenancePage$ = combineLatest([this.maintenancePageOn$, this.secondsTilMaintenance$, this.listenConfigLoaded$]).pipe(filter(([_, __, loaded]) => loaded), // Avoid false-firing if we don't have config yet
        map(([pageOverride, s]) => {
            if (isNotNil(pageOverride))
                return pageOverride;
            return s ? s <= 0 : false;
        }), distinctUntilChanged());
        this.minsTilMaintenance$ = this.secondsTilMaintenance$.pipe(map((seconds) => (seconds ? Math.ceil(seconds / 60) : seconds)), distinctUntilChanged() // Prevents refiring every second
        );
        // EFFECTS
        this.onInitStaticConfig = this.effect((_$) => _$.pipe(switchMap((_) => {
            return from(this.configService.getAll()).pipe(tapResponse((config) => {
                this.logger.log('ConfigStore - onInitStaticConfig -', config);
                return this.onStaticConfigFetched(config);
            }, (error) => this.errorHandler.handleError(error)));
        })));
        this.onInitListenConfig = this.effect((_$) => _$.pipe(switchMap((_) => {
            this.logger.log('ConfigStore - onInitListenConfig');
            return this.listenConfigService.listenMainConfiguration().pipe(tapResponse((listenConfig) => {
                // this.logger.log('ConfigStore - listenMainConfiguration -', listenConfig)
                return this.onListenConfigChanged(listenConfig);
            }, (error) => this.errorHandler.handleError(error)));
        })));
        /**
         * Route to maintenance page when it needs to show and we are configured to do so
         * If routing is done, stash the route we navigated from and route back there when maintenance is over.
         * If page is on maintenance on first load, route home when maintenance is over
         */
        this.listenMaintenanceAndRoute = this.effect((showMaintenancePage$) => showMaintenancePage$.pipe(distinctUntilChanged(), // Only fire when showMaintenancePage has just switched on or off
        withLatestFrom(this.routeOnMaintenance$, this.preMaintenanceRoute$, this.maintenanceFullReload$), tap(([showMaintenancePage, routeOnMaintenance, preMaintenanceRoute, fullReload]) => {
            const currentRoute = this.router.url;
            if (currentRoute === this.configNavigationService.maintenanceRoute && !showMaintenancePage) {
                this.logger.log('Routing back from maintenance', { currentRoute, preMaintenanceRoute });
                this.setPreMaintenanceRoute(null);
                return this.configNavigationService.routeWithConfig(preMaintenanceRoute ?? this.configNavigationService.home, { fullReload });
            }
            if (showMaintenancePage && routeOnMaintenance && currentRoute !== this.configNavigationService.maintenanceRoute) {
                this.logger.log('Setting pre-maintenance route', { currentRoute, preMaintenanceRoute });
                this.setPreMaintenanceRoute(currentRoute);
                return this.configNavigationService.toMaintenance();
            }
            return;
        })));
        // UPDATERS
        this.onStaticConfigFetched = this.updater((state, staticConfig) => ({
            ...state,
            staticConfig,
            staticConfigLoaded: true,
        }));
        this.onListenConfigChanged = this.updater((state, listenConfig) => ({
            ...state,
            listenConfig,
            listenConfigLoaded: true,
        }));
        this.setPreMaintenanceRoute = this.updater((state, preMaintenanceRoute) => ({ ...state, preMaintenanceRoute }));
        this.onInitStaticConfig();
        this.onInitListenConfig();
        this.listenMaintenanceAndRoute(this.showMaintenancePage$);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ConfigStore, deps: [{ token: i1$2.E11ErrorHandlerService }, { token: i1$2.E11Logger }, { token: BaseConfigService }, { token: ListenConfigService }, { token: ConfigNavigationService }, { token: i1$1.Router }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ConfigStore, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ConfigStore, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: i1$2.E11ErrorHandlerService }, { type: i1$2.E11Logger }, { type: BaseConfigService }, { type: ListenConfigService }, { type: ConfigNavigationService }, { type: i1$1.Router }] });

class ConfigToastService {
    constructor(toastService) {
        this.toastService = toastService;
    }
    showMaintenanceToast(minutesToMaintenance) {
        this.toastService.popToastMessage({
            title: 'Scheduled maintenance...',
            bodyTitle: `We are scheduling maintenance in ${minutesToMaintenance} minute${minutesToMaintenance !== 1 ? 's' : ''}`,
            bodyContent: `Brew some coffee, go for a walk... We'll be back before you know it!`,
            dismissType: ToastDismissType.CtaOnly,
            cta: 'scheduledMaintenance',
            ctaText: 'Okay!',
            icon: 'engineering',
            dismissOnRouteChange: false,
        });
    }
    closeToast() {
        this.toastService.closeToast();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ConfigToastService, deps: [{ token: i1$3.E11ToastService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ConfigToastService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ConfigToastService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: i1$3.E11ToastService }] });

class UiMaintenanceGearComponent {
    constructor() { }
    ngOnInit() { }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: UiMaintenanceGearComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.9", type: UiMaintenanceGearComponent, selector: "ui-maintenance-gear", ngImport: i0, template: `
    <svg
      class="e11-w-full e11-h-full e11-text-skin-primary"
      fill="currentColor"
      stroke="currentColor"
      width="621.410535px"
      height="510.824748px"
      viewBox="0 0 621.410535 510.824748"
      version="1.1"
      xmlns="http://www.w3.org/2000/svg"
      xmlns:xlink="http://www.w3.org/1999/xlink"
    >
      <g id="maintenance-page-cog-group">
        <g id="Maintenance-page-cogs" transform="translate(-44.4099, -70.9957)">
          <g id="Group" transform="translate(44.4099, 70.9957)">
            <path
              d="M236.297166,31.2971665 C240.715444,31.2971665 244.297166,34.8788885 244.297166,39.2971665 L244.298009,63.2069174 C262.153904,67.0338999 279.457686,74.0823338 295.279846,84.3522189 L311.792116,67.8402763 C314.91631,64.716082 319.98163,64.716082 323.105824,67.8402763 L357.754057,102.488509 C360.878251,105.612703 360.878251,110.678023 357.754057,113.802217 L341.089996,130.468756 C351.003922,145.970224 357.846422,162.868258 361.617493,180.29752 L386.297166,180.297166 C390.715444,180.297166 394.297166,183.878888 394.297166,188.297166 L394.297166,237.297166 C394.297166,241.715444 390.715444,245.297166 386.297166,245.297166 L361.617277,245.297812 C357.846079,262.727095 351.003439,279.625132 341.089357,295.126576 L358.461163,312.499223 C361.585358,315.623417 361.585358,320.688737 358.461163,323.812931 L323.812931,358.461163 C320.688737,361.585358 315.623417,361.585358 312.499223,358.461163 L295.28085,341.241462 C279.458418,351.511701 262.154282,358.560352 244.298009,362.387416 L244.297166,386.297166 C244.297166,390.715444 240.715444,394.297166 236.297166,394.297166 L187.297166,394.297166 C182.878888,394.297166 179.297166,390.715444 179.297166,386.297166 L179.297543,362.260933 C161.971867,358.476768 145.176276,351.655491 129.761649,341.797103 L112.388004,359.16827 C109.263809,362.292464 104.198489,362.292464 101.074295,359.16827 L66.4260628,324.520038 C63.3018684,321.395844 63.3018684,316.330524 66.4260628,313.206329 L83.6451122,295.986953 C73.4313516,280.25126 66.4039283,263.050096 62.5628422,245.297812 L39.2971665,245.297166 C34.8788885,245.297166 31.2971665,241.715444 31.2971665,237.297166 L31.2971665,188.297166 C31.2971665,183.878888 34.8788885,180.297166 39.2971665,180.297166 L62.5626261,180.29752 C66.4037464,162.544505 73.431459,145.342636 83.6457639,129.606376 L67.1331695,113.09511 C64.0089752,109.970916 64.0089752,104.905596 67.1331695,101.781402 L101.781402,67.1331695 C104.905596,64.0089752 109.970916,64.0089752 113.09511,67.1331695 L129.76065,83.7978695 C145.17555,73.9391371 161.971492,67.1176471 179.297543,63.3333995 L179.297166,39.2971665 C179.297166,34.8788885 182.878888,31.2971665 187.297166,31.2971665 L236.297166,31.2971665 Z M213.09006,112.004273 C157.585442,112.004273 112.59006,156.999656 112.59006,212.504273 C112.59006,223.987744 114.516059,235.021373 118.062651,245.299755 L117.783273,245.298365 C119.125382,249.289383 120.727627,253.212171 122.590007,257.040282 L122.845446,256.784007 C132.648937,276.726318 148.868015,292.945396 168.810326,302.748887 L168.554051,303.004326 C172.062246,304.711067 175.649956,306.199335 179.296824,307.469129 L179.295754,307.181091 C189.856907,310.951169 201.233719,313.004273 213.09006,313.004273 C268.594677,313.004273 313.59006,268.008891 313.59006,212.504273 C313.59006,156.999656 268.594677,112.004273 213.09006,112.004273 Z"
              id="Combined-Shape"
              transform="translate(212.7972, 212.7972) rotate(11) translate(-212.7972, -212.7972)"
            ></path>
            <path
              d="M478.995018,50.0042732 C483.413296,50.0042732 486.995018,53.5859952 486.995018,58.0042732 L486.994928,64.5965091 C495.160612,66.3465681 503.073812,69.5698584 510.309437,74.26638 L514.790504,69.7854207 C517.914698,66.6612264 522.980018,66.6612264 526.104213,69.7854207 L535.808912,79.4901204 C538.933107,82.6143147 538.933107,87.6796345 535.808912,90.8038289 L531.258074,95.354949 C535.791724,102.44365 538.920847,110.170994 540.645442,118.141277 L547.59006,118.142014 C552.008338,118.142014 555.59006,121.723736 555.59006,126.142014 L555.59006,139.866532 C555.59006,144.28481 552.008338,147.866532 547.59006,147.866532 L540.645442,147.867269 C538.920928,155.837178 535.792018,163.56417 531.258713,170.652597 L536.132272,175.528078 C539.256467,178.652272 539.256467,183.717592 536.132272,186.841786 L526.427573,196.546486 C523.303378,199.67068 518.238059,199.67068 515.113864,196.546486 L510.309437,191.742166 C503.073812,196.438688 495.160612,199.661978 486.994928,201.412037 L486.995018,208.004273 C486.995018,212.422551 483.413296,216.004273 478.995018,216.004273 L465.2705,216.004273 C460.852222,216.004273 457.2705,212.422551 457.2705,208.004273 L457.270682,201.354097 C449.347669,199.623603 441.667063,196.504246 434.617966,191.996025 L429.742895,196.869846 C426.618701,199.99404 421.553381,199.99404 418.429187,196.869846 L408.724487,187.165146 C405.600293,184.040952 405.600293,178.975632 408.724487,175.851438 L413.528806,171.047011 C408.858126,163.851198 405.64451,155.985247 403.887957,147.867269 L397.59006,147.866532 C393.171782,147.866532 389.59006,144.28481 389.59006,139.866532 L389.59006,126.142014 C389.59006,121.723736 393.171782,118.142014 397.59006,118.142014 L403.887957,118.141277 C405.64451,110.0233 408.858126,102.157349 413.528806,94.9615355 L409.047847,90.4804687 C405.923653,87.3562744 405.923653,82.2909546 409.047847,79.1667602 L418.752547,69.4620606 C421.876741,66.3378662 426.942061,66.3378662 430.066255,69.4620606 L434.617966,74.0125211 C441.667063,69.5043005 449.347669,66.3849432 457.270682,64.6544494 L457.2705,58.0042732 C457.2705,53.5859952 460.852222,50.0042732 465.2705,50.0042732 L478.995018,50.0042732 Z M472.59006,87.0042732 C447.184961,87.0042732 426.59006,107.599175 426.59006,133.004273 C426.59006,158.409372 447.184961,179.004273 472.59006,179.004273 C497.995158,179.004273 518.59006,158.409372 518.59006,133.004273 C518.59006,107.599175 497.995158,87.0042732 472.59006,87.0042732 Z"
              id="Combined-Shape"
              transform="translate(472.5901, 133.0043) rotate(-12) translate(-472.5901, -133.0043)"
            ></path>
            <path
              d="M483.333861,254.004273 C487.752139,254.004273 491.333861,257.585995 491.333861,262.004273 L491.333993,272.991814 C501.959015,275.268987 512.255513,279.463063 521.670386,285.574042 L529.205353,278.039726 C532.329547,274.915532 537.394867,274.915532 540.519062,278.039726 L556.554607,294.075271 C559.678801,297.199466 559.678801,302.264786 556.554607,305.38898 L548.929826,313.015828 C554.828921,322.239781 558.900423,332.294727 561.144331,342.665768 L572.59006,342.66543 C577.008338,342.66543 580.59006,346.247152 580.59006,350.66543 L580.59006,373.343116 C580.59006,377.761394 577.008338,381.343116 572.59006,381.343116 L561.144331,381.342778 C558.900423,391.713819 554.828921,401.768765 548.929826,410.992718 L556.975365,419.040324 C560.099559,422.164519 560.099559,427.229839 556.975365,430.354033 L540.939819,446.389578 C537.815625,449.513773 532.750305,449.513773 529.626111,446.389578 L521.670386,438.434504 C512.255513,444.545484 501.959015,448.73956 491.333993,451.016732 L491.333861,462.004273 C491.333861,466.422551 487.752139,470.004273 483.333861,470.004273 L460.656175,470.004273 C456.237897,470.004273 452.656175,466.422551 452.656175,462.004273 L452.656862,450.941492 C442.347494,448.68984 432.353533,444.631029 423.181266,438.765058 L415.133251,446.810336 C412.009056,449.93453 406.943737,449.93453 403.819542,446.810336 L387.783997,430.774791 C384.659802,427.650596 384.659802,422.585276 387.783997,419.461082 L395.739071,411.505357 C389.66142,402.141831 385.479821,391.906272 383.194272,381.342778 L372.59006,381.343116 C368.171782,381.343116 364.59006,377.761394 364.59006,373.343116 L364.59006,350.66543 C364.59006,346.247152 368.171782,342.66543 372.59006,342.66543 L383.194272,342.665768 C385.479821,332.102274 389.66142,321.866715 395.739071,312.503189 L388.204755,304.968222 C385.08056,301.844028 385.08056,296.778708 388.204755,293.654514 L404.2403,277.618968 C407.364494,274.494774 412.429814,274.494774 415.554009,277.618968 L423.180266,285.244127 C432.352806,279.377812 442.347119,275.318788 452.656862,273.067055 L452.656175,262.004273 C452.656175,257.585995 456.237897,254.004273 460.656175,254.004273 L483.333861,254.004273 Z M472.59006,302.004273 C439.452975,302.004273 412.59006,328.867188 412.59006,362.004273 C412.59006,395.141358 439.452975,422.004273 472.59006,422.004273 C505.727145,422.004273 532.59006,395.141358 532.59006,362.004273 C532.59006,328.867188 505.727145,302.004273 472.59006,302.004273 Z"
              id="Combined-Shape"
              transform="translate(472.5901, 362.0043) rotate(32) translate(-472.5901, -362.0043)"
            ></path>
          </g>
        </g>
      </g>
    </svg>
  `, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: UiMaintenanceGearComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'ui-maintenance-gear',
                    template: `
    <svg
      class="e11-w-full e11-h-full e11-text-skin-primary"
      fill="currentColor"
      stroke="currentColor"
      width="621.410535px"
      height="510.824748px"
      viewBox="0 0 621.410535 510.824748"
      version="1.1"
      xmlns="http://www.w3.org/2000/svg"
      xmlns:xlink="http://www.w3.org/1999/xlink"
    >
      <g id="maintenance-page-cog-group">
        <g id="Maintenance-page-cogs" transform="translate(-44.4099, -70.9957)">
          <g id="Group" transform="translate(44.4099, 70.9957)">
            <path
              d="M236.297166,31.2971665 C240.715444,31.2971665 244.297166,34.8788885 244.297166,39.2971665 L244.298009,63.2069174 C262.153904,67.0338999 279.457686,74.0823338 295.279846,84.3522189 L311.792116,67.8402763 C314.91631,64.716082 319.98163,64.716082 323.105824,67.8402763 L357.754057,102.488509 C360.878251,105.612703 360.878251,110.678023 357.754057,113.802217 L341.089996,130.468756 C351.003922,145.970224 357.846422,162.868258 361.617493,180.29752 L386.297166,180.297166 C390.715444,180.297166 394.297166,183.878888 394.297166,188.297166 L394.297166,237.297166 C394.297166,241.715444 390.715444,245.297166 386.297166,245.297166 L361.617277,245.297812 C357.846079,262.727095 351.003439,279.625132 341.089357,295.126576 L358.461163,312.499223 C361.585358,315.623417 361.585358,320.688737 358.461163,323.812931 L323.812931,358.461163 C320.688737,361.585358 315.623417,361.585358 312.499223,358.461163 L295.28085,341.241462 C279.458418,351.511701 262.154282,358.560352 244.298009,362.387416 L244.297166,386.297166 C244.297166,390.715444 240.715444,394.297166 236.297166,394.297166 L187.297166,394.297166 C182.878888,394.297166 179.297166,390.715444 179.297166,386.297166 L179.297543,362.260933 C161.971867,358.476768 145.176276,351.655491 129.761649,341.797103 L112.388004,359.16827 C109.263809,362.292464 104.198489,362.292464 101.074295,359.16827 L66.4260628,324.520038 C63.3018684,321.395844 63.3018684,316.330524 66.4260628,313.206329 L83.6451122,295.986953 C73.4313516,280.25126 66.4039283,263.050096 62.5628422,245.297812 L39.2971665,245.297166 C34.8788885,245.297166 31.2971665,241.715444 31.2971665,237.297166 L31.2971665,188.297166 C31.2971665,183.878888 34.8788885,180.297166 39.2971665,180.297166 L62.5626261,180.29752 C66.4037464,162.544505 73.431459,145.342636 83.6457639,129.606376 L67.1331695,113.09511 C64.0089752,109.970916 64.0089752,104.905596 67.1331695,101.781402 L101.781402,67.1331695 C104.905596,64.0089752 109.970916,64.0089752 113.09511,67.1331695 L129.76065,83.7978695 C145.17555,73.9391371 161.971492,67.1176471 179.297543,63.3333995 L179.297166,39.2971665 C179.297166,34.8788885 182.878888,31.2971665 187.297166,31.2971665 L236.297166,31.2971665 Z M213.09006,112.004273 C157.585442,112.004273 112.59006,156.999656 112.59006,212.504273 C112.59006,223.987744 114.516059,235.021373 118.062651,245.299755 L117.783273,245.298365 C119.125382,249.289383 120.727627,253.212171 122.590007,257.040282 L122.845446,256.784007 C132.648937,276.726318 148.868015,292.945396 168.810326,302.748887 L168.554051,303.004326 C172.062246,304.711067 175.649956,306.199335 179.296824,307.469129 L179.295754,307.181091 C189.856907,310.951169 201.233719,313.004273 213.09006,313.004273 C268.594677,313.004273 313.59006,268.008891 313.59006,212.504273 C313.59006,156.999656 268.594677,112.004273 213.09006,112.004273 Z"
              id="Combined-Shape"
              transform="translate(212.7972, 212.7972) rotate(11) translate(-212.7972, -212.7972)"
            ></path>
            <path
              d="M478.995018,50.0042732 C483.413296,50.0042732 486.995018,53.5859952 486.995018,58.0042732 L486.994928,64.5965091 C495.160612,66.3465681 503.073812,69.5698584 510.309437,74.26638 L514.790504,69.7854207 C517.914698,66.6612264 522.980018,66.6612264 526.104213,69.7854207 L535.808912,79.4901204 C538.933107,82.6143147 538.933107,87.6796345 535.808912,90.8038289 L531.258074,95.354949 C535.791724,102.44365 538.920847,110.170994 540.645442,118.141277 L547.59006,118.142014 C552.008338,118.142014 555.59006,121.723736 555.59006,126.142014 L555.59006,139.866532 C555.59006,144.28481 552.008338,147.866532 547.59006,147.866532 L540.645442,147.867269 C538.920928,155.837178 535.792018,163.56417 531.258713,170.652597 L536.132272,175.528078 C539.256467,178.652272 539.256467,183.717592 536.132272,186.841786 L526.427573,196.546486 C523.303378,199.67068 518.238059,199.67068 515.113864,196.546486 L510.309437,191.742166 C503.073812,196.438688 495.160612,199.661978 486.994928,201.412037 L486.995018,208.004273 C486.995018,212.422551 483.413296,216.004273 478.995018,216.004273 L465.2705,216.004273 C460.852222,216.004273 457.2705,212.422551 457.2705,208.004273 L457.270682,201.354097 C449.347669,199.623603 441.667063,196.504246 434.617966,191.996025 L429.742895,196.869846 C426.618701,199.99404 421.553381,199.99404 418.429187,196.869846 L408.724487,187.165146 C405.600293,184.040952 405.600293,178.975632 408.724487,175.851438 L413.528806,171.047011 C408.858126,163.851198 405.64451,155.985247 403.887957,147.867269 L397.59006,147.866532 C393.171782,147.866532 389.59006,144.28481 389.59006,139.866532 L389.59006,126.142014 C389.59006,121.723736 393.171782,118.142014 397.59006,118.142014 L403.887957,118.141277 C405.64451,110.0233 408.858126,102.157349 413.528806,94.9615355 L409.047847,90.4804687 C405.923653,87.3562744 405.923653,82.2909546 409.047847,79.1667602 L418.752547,69.4620606 C421.876741,66.3378662 426.942061,66.3378662 430.066255,69.4620606 L434.617966,74.0125211 C441.667063,69.5043005 449.347669,66.3849432 457.270682,64.6544494 L457.2705,58.0042732 C457.2705,53.5859952 460.852222,50.0042732 465.2705,50.0042732 L478.995018,50.0042732 Z M472.59006,87.0042732 C447.184961,87.0042732 426.59006,107.599175 426.59006,133.004273 C426.59006,158.409372 447.184961,179.004273 472.59006,179.004273 C497.995158,179.004273 518.59006,158.409372 518.59006,133.004273 C518.59006,107.599175 497.995158,87.0042732 472.59006,87.0042732 Z"
              id="Combined-Shape"
              transform="translate(472.5901, 133.0043) rotate(-12) translate(-472.5901, -133.0043)"
            ></path>
            <path
              d="M483.333861,254.004273 C487.752139,254.004273 491.333861,257.585995 491.333861,262.004273 L491.333993,272.991814 C501.959015,275.268987 512.255513,279.463063 521.670386,285.574042 L529.205353,278.039726 C532.329547,274.915532 537.394867,274.915532 540.519062,278.039726 L556.554607,294.075271 C559.678801,297.199466 559.678801,302.264786 556.554607,305.38898 L548.929826,313.015828 C554.828921,322.239781 558.900423,332.294727 561.144331,342.665768 L572.59006,342.66543 C577.008338,342.66543 580.59006,346.247152 580.59006,350.66543 L580.59006,373.343116 C580.59006,377.761394 577.008338,381.343116 572.59006,381.343116 L561.144331,381.342778 C558.900423,391.713819 554.828921,401.768765 548.929826,410.992718 L556.975365,419.040324 C560.099559,422.164519 560.099559,427.229839 556.975365,430.354033 L540.939819,446.389578 C537.815625,449.513773 532.750305,449.513773 529.626111,446.389578 L521.670386,438.434504 C512.255513,444.545484 501.959015,448.73956 491.333993,451.016732 L491.333861,462.004273 C491.333861,466.422551 487.752139,470.004273 483.333861,470.004273 L460.656175,470.004273 C456.237897,470.004273 452.656175,466.422551 452.656175,462.004273 L452.656862,450.941492 C442.347494,448.68984 432.353533,444.631029 423.181266,438.765058 L415.133251,446.810336 C412.009056,449.93453 406.943737,449.93453 403.819542,446.810336 L387.783997,430.774791 C384.659802,427.650596 384.659802,422.585276 387.783997,419.461082 L395.739071,411.505357 C389.66142,402.141831 385.479821,391.906272 383.194272,381.342778 L372.59006,381.343116 C368.171782,381.343116 364.59006,377.761394 364.59006,373.343116 L364.59006,350.66543 C364.59006,346.247152 368.171782,342.66543 372.59006,342.66543 L383.194272,342.665768 C385.479821,332.102274 389.66142,321.866715 395.739071,312.503189 L388.204755,304.968222 C385.08056,301.844028 385.08056,296.778708 388.204755,293.654514 L404.2403,277.618968 C407.364494,274.494774 412.429814,274.494774 415.554009,277.618968 L423.180266,285.244127 C432.352806,279.377812 442.347119,275.318788 452.656862,273.067055 L452.656175,262.004273 C452.656175,257.585995 456.237897,254.004273 460.656175,254.004273 L483.333861,254.004273 Z M472.59006,302.004273 C439.452975,302.004273 412.59006,328.867188 412.59006,362.004273 C412.59006,395.141358 439.452975,422.004273 472.59006,422.004273 C505.727145,422.004273 532.59006,395.141358 532.59006,362.004273 C532.59006,328.867188 505.727145,302.004273 472.59006,302.004273 Z"
              id="Combined-Shape"
              transform="translate(472.5901, 362.0043) rotate(32) translate(-472.5901, -362.0043)"
            ></path>
          </g>
        </g>
      </g>
    </svg>
  `,
                }]
        }], ctorParameters: () => [] });

class UiMaintenancePageComponent {
    constructor() {
        this.BASE_SDK_IMAGE_URL = BASE_SDK_IMAGE_URL;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: UiMaintenancePageComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.9", type: UiMaintenancePageComponent, selector: "ui-maintenance-page", inputs: { companyLogoUrl: "companyLogoUrl" }, ngImport: i0, template: `
    <div class="e11-flex e11-justify-end e11-w-full e11-p-14">
      <img *ngIf="companyLogoUrl" alt="Company Logo" src="{{ companyLogoUrl }}" width="200" />
    </div>
    <div class="e11-grid e11-grid-cols-1 md:e11-grid-cols-2 e11-pl-6 e11-mb-12">
      <div class="md:e11-border-r-4 md:e11-border-skin-primary-accent md:e11-pr-6">
        <div class="e11-m-2">
          <div class="e11-relative e11-flex e11-flex-row e11-items-end">
            <div class="e11-flex e11-flex-row">
              <h3 class="e11-hidden md:e11-flex e11-mb-6 e11-pl-6 e11-font-bold e11-text-skin-primary-accent e11-text-2xl e11-w-44">
                {{ 'HOLD TIGHT!' | translate }}
              </h3>
            </div>

            <!-- <img src="{{ BASE_SDK_IMAGE_URL + 'maintenance-gear.png' }}" alt="Gear Icon" width="75%" class="e11-hidden md:e11-flex e11-pr-6" /> -->
            <ui-maintenance-gear></ui-maintenance-gear>
          </div>
          <div class="e11-flex e11-flex-col e11-pb-6 e11-pl-6 e11-text-skin-primary-accent e11-text-xl">
            <h1 class="e11-pb-2 e11-text-4xl">
              {{ 'Under Maintenance,' | translate }}
            </h1>
            <p class="e11-text-skin-secondary">
              {{ 'Walk the dog, put the kettle on...' | translate }}
              <br />
            </p>
          </div>
        </div>
      </div>

      <div class="e11-hidden md:e11-flex e11-pl-8 e11-flex-col e11-justify-center e11-items-center">
        <span class="e11-pb-8 e11-mb-8 e11-text-skin-secondary e11-text-5xl">
          {{ "We'll be back momentarily!" | translate }}
        </span>
      </div>
    </div>
  `, isInline: true, dependencies: [{ kind: "directive", type: i4.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: UiMaintenanceGearComponent, selector: "ui-maintenance-gear" }, { kind: "pipe", type: i3.TranslatePipe, name: "translate" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: UiMaintenancePageComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'ui-maintenance-page',
                    template: `
    <div class="e11-flex e11-justify-end e11-w-full e11-p-14">
      <img *ngIf="companyLogoUrl" alt="Company Logo" src="{{ companyLogoUrl }}" width="200" />
    </div>
    <div class="e11-grid e11-grid-cols-1 md:e11-grid-cols-2 e11-pl-6 e11-mb-12">
      <div class="md:e11-border-r-4 md:e11-border-skin-primary-accent md:e11-pr-6">
        <div class="e11-m-2">
          <div class="e11-relative e11-flex e11-flex-row e11-items-end">
            <div class="e11-flex e11-flex-row">
              <h3 class="e11-hidden md:e11-flex e11-mb-6 e11-pl-6 e11-font-bold e11-text-skin-primary-accent e11-text-2xl e11-w-44">
                {{ 'HOLD TIGHT!' | translate }}
              </h3>
            </div>

            <!-- <img src="{{ BASE_SDK_IMAGE_URL + 'maintenance-gear.png' }}" alt="Gear Icon" width="75%" class="e11-hidden md:e11-flex e11-pr-6" /> -->
            <ui-maintenance-gear></ui-maintenance-gear>
          </div>
          <div class="e11-flex e11-flex-col e11-pb-6 e11-pl-6 e11-text-skin-primary-accent e11-text-xl">
            <h1 class="e11-pb-2 e11-text-4xl">
              {{ 'Under Maintenance,' | translate }}
            </h1>
            <p class="e11-text-skin-secondary">
              {{ 'Walk the dog, put the kettle on...' | translate }}
              <br />
            </p>
          </div>
        </div>
      </div>

      <div class="e11-hidden md:e11-flex e11-pl-8 e11-flex-col e11-justify-center e11-items-center">
        <span class="e11-pb-8 e11-mb-8 e11-text-skin-secondary e11-text-5xl">
          {{ "We'll be back momentarily!" | translate }}
        </span>
      </div>
    </div>
  `,
                }]
        }], ctorParameters: () => [], propDecorators: { companyLogoUrl: [{
                type: Input
            }] } });

const MAINTENANCE_TEMPLATE_KEY = 'maintenance';
class MaintenancePageComponent extends E11TemplateMap {
    constructor(configStore, toastService, logger) {
        super();
        this.configStore = configStore;
        this.toastService = toastService;
        this.logger = logger;
        this.forcePageOn = false; // ! Purely for testing & debugging purposes
        this.intervals = [15, 10, 5, 1]; // minutes before scheduled maintenance time to alert
        this.shownToast = false;
        this.MAINTENANCE_TEMPLATE_KEY = MAINTENANCE_TEMPLATE_KEY;
        this.destroy$ = new Subject();
        this.showMaintenancePage$ = this.configStore.showMaintenancePage$;
        this.minsTilMaintenance$ = this.configStore.minsTilMaintenance$;
    }
    ngOnInit() {
        this.minsTilMaintenance$.pipe(takeUntil(this.destroy$)).subscribe((minsTilMaintenance) => {
            this.logger.log('MaintenancePageComponent - minsTilMaintenance', minsTilMaintenance);
            if (isNil(minsTilMaintenance) || minsTilMaintenance < 0)
                return;
            else if (this.intervals.includes(minsTilMaintenance))
                this.showToast(minsTilMaintenance);
            else if (!this.shownToast && minsTilMaintenance > 0 && minsTilMaintenance < Math.max(...this.intervals))
                this.showToast(minsTilMaintenance);
            else if (minsTilMaintenance === 0)
                this.reset();
        });
    }
    ngOnDestroy() {
        this.destroy$.next(true);
        this.destroy$.complete();
    }
    reset() {
        this.logger.log('MaintenancePageComponent - resetting');
        this.removeToast();
        this.shownToast = false;
    }
    showToast(minutesToMaintenance) {
        this.logger.log('MaintenancePageComponent - showToast', { minutesToMaintenance });
        this.shownToast = true;
        this.toastService.showMaintenanceToast(minutesToMaintenance);
    }
    removeToast() {
        this.toastService.closeToast();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: MaintenancePageComponent, deps: [{ token: ConfigStore }, { token: ConfigToastService }, { token: i1$2.E11Logger }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.9", type: MaintenancePageComponent, selector: "sdk-config-maintenance-page", inputs: { companyLogoUrl: "companyLogoUrl", forcePageOn: "forcePageOn" }, usesInheritance: true, ngImport: i0, template: `
    <div *ngIf="(showMaintenancePage$ | async) || forcePageOn" class="e11-bg-white e11-z-[2000] e11-fixed e11-inset-0" [@animationFadeInOut]>
      <ng-container *ngTemplateOutlet="e11TemplateMap[MAINTENANCE_TEMPLATE_KEY]"></ng-container>
      <ui-maintenance-page *ngIf="!e11TemplateMap[MAINTENANCE_TEMPLATE_KEY]" [companyLogoUrl]="companyLogoUrl"></ui-maintenance-page>
    </div>
  `, isInline: true, dependencies: [{ kind: "directive", type: i4.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i4.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "component", type: UiMaintenancePageComponent, selector: "ui-maintenance-page", inputs: ["companyLogoUrl"] }, { kind: "pipe", type: i4.AsyncPipe, name: "async" }], animations: [animationFadeInOut()] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: MaintenancePageComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'sdk-config-maintenance-page',
                    template: `
    <div *ngIf="(showMaintenancePage$ | async) || forcePageOn" class="e11-bg-white e11-z-[2000] e11-fixed e11-inset-0" [@animationFadeInOut]>
      <ng-container *ngTemplateOutlet="e11TemplateMap[MAINTENANCE_TEMPLATE_KEY]"></ng-container>
      <ui-maintenance-page *ngIf="!e11TemplateMap[MAINTENANCE_TEMPLATE_KEY]" [companyLogoUrl]="companyLogoUrl"></ui-maintenance-page>
    </div>
  `,
                    animations: [animationFadeInOut()],
                }]
        }], ctorParameters: () => [{ type: ConfigStore }, { type: ConfigToastService }, { type: i1$2.E11Logger }], propDecorators: { companyLogoUrl: [{
                type: Input
            }], forcePageOn: [{
                type: Input
            }] } });

const canActivateMaintenance = async (route, state) => {
    const configStore = inject(ConfigStore);
    const navigationService = inject(ConfigNavigationService);
    const showMaintenancePage = await firstValueFrom(configStore.showMaintenancePage$);
    const preMaintenanceRoute = await firstValueFrom(configStore.preMaintenanceRoute$);
    if (!showMaintenancePage)
        navigationService.routeWithConfig(preMaintenanceRoute ?? navigationService.home);
    return true;
};

const ROUTES = [
    {
        path: CONFIG_ROUTES.MAINTENANCE,
        component: UiMaintenancePageComponent,
        canActivate: [canActivateMaintenance],
    },
];
class ConfigRoutingModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ConfigRoutingModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.9", ngImport: i0, type: ConfigRoutingModule, imports: [i1$1.RouterModule], exports: [RouterModule] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ConfigRoutingModule, imports: [RouterModule.forChild(ROUTES), RouterModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ConfigRoutingModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [],
                    imports: [RouterModule.forChild(ROUTES)],
                    exports: [RouterModule],
                }]
        }] });

const DeclareExportComponents = [MaintenancePageComponent];
class ConfigModule {
    static forRoot(config) {
        return {
            ngModule: ConfigModule,
            providers: [
                {
                    provide: CONFIG_CONSTRUCTOR,
                    useValue: config.configModel,
                },
                {
                    provide: BaseConfigProvider,
                    useClass: config.configProvider,
                },
            ],
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ConfigModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.9", ngImport: i0, type: ConfigModule, declarations: [MaintenancePageComponent, UiMaintenancePageComponent, UiMaintenanceGearComponent], imports: [CommonModule, TranslateModule, E11UtilModule, ConfigRoutingModule], exports: [MaintenancePageComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ConfigModule, providers: [
            provideRemoteConfig(() => {
                const remoteConfig = getRemoteConfig();
                remoteConfig.settings = isDevMode() ? { ...remoteConfig.settings, minimumFetchIntervalMillis: 10_000 } : { ...remoteConfig.settings };
                return remoteConfig;
            }),
        ], imports: [CommonModule, TranslateModule, E11UtilModule, ConfigRoutingModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ConfigModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [...DeclareExportComponents, UiMaintenancePageComponent, UiMaintenanceGearComponent],
                    imports: [CommonModule, TranslateModule, E11UtilModule, ConfigRoutingModule],
                    providers: [
                        provideRemoteConfig(() => {
                            const remoteConfig = getRemoteConfig();
                            remoteConfig.settings = isDevMode() ? { ...remoteConfig.settings, minimumFetchIntervalMillis: 10_000 } : { ...remoteConfig.settings };
                            return remoteConfig;
                        }),
                    ],
                    exports: [...DeclareExportComponents],
                }]
        }] });

class FeatureGuard {
    constructor(router, configStore, configProvider, errorHandler, logger) {
        this.router = router;
        this.configStore = configStore;
        this.configProvider = configProvider;
        this.errorHandler = errorHandler;
        this.logger = logger;
        this.notFoundRoute = this.configProvider.config.notFoundRoute;
    }
    canActivate(route, state) {
        const feature = route.data.feature;
        if (!feature)
            return true;
        const features$ = this.configStore.staticConfigLoaded$.pipe(filter(isTrue), switchMap(() => {
            return this.configStore.features$;
        }));
        return features$.pipe(map((features) => {
            if (!features) {
                this.errorHandler.error('Features not present, but static config loaded. Should investigate');
                return false;
            }
            if (features[feature])
                return true;
            this.router.navigate([this.notFoundRoute]);
            this.logger.warn('Attempted to go to route blocked by FeatureGuard for: ', feature, 'Available features are: ', features);
            return false;
        }));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: FeatureGuard, deps: [{ token: i1$1.Router }, { token: ConfigStore }, { token: BaseConfigProvider }, { token: i1$2.E11ErrorHandlerService }, { token: i1$2.E11Logger }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: FeatureGuard, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: FeatureGuard, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: () => [{ type: i1$1.Router }, { type: ConfigStore }, { type: BaseConfigProvider }, { type: i1$2.E11ErrorHandlerService }, { type: i1$2.E11Logger }] });

/*
 * Public API Surface of config
 */

/**
 * Generated bundle index. Do not edit.
 */

export { BaseConfigProvider, BaseConfigService, BaseFeatureService, Boolean, ConfigModule, ConfigProvider, ConfigStore, Default, FeatureGuard, Json, ListenConfigService, MAINTENANCE_TEMPLATE_KEY, MainConfigNotFoundError, MaintenancePageComponent, Number, String, getDefault, isBoolean, isJson, isNumber, isString };
